// Auth.tsx - v1.0.2 - Native deep link redirect fix
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AlertTriangle, Shield, Lock, Heart, Brain, Sparkles, Moon, Users, MessageCircle, Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';
import therappiTextLogo from '@/assets/therappi-text-logo.svg';
import { LogoEditorControls } from '@/components/LogoEditor';
import { useLogoEditor } from '@/contexts/LogoEditorContext';
import { AgeVerification } from '@/components/AgeVerification';
import { SEO } from '@/components/SEO';

/**
 * Get the appropriate email redirect URL based on platform
 * Native apps should use custom URL scheme, web uses origin
 * 
 * NOTE: For this to work, 'therappi://' must be added to Supabase's
 * allowed redirect URLs in the dashboard: Authentication > URL Configuration
 */
const getEmailRedirectUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    // Use custom URL scheme for native apps
    // This will redirect to therappi://auth which the app can handle
    return 'therappi://auth';
  }
  // Web uses origin
  return `${window.location.origin}/`;
};

export default function Auth() {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP
  const navigate = useNavigate();
  const { isEditing, setIsEditing } = useLogoEditor();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // SECURITY FIX: Clear credentials on mount to prevent pre-filled fields
  // This runs on EVERY mount of the Auth component
  useEffect(() => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError('');
  }, []);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    checkAuth();
  }, []);
  const [parentEmail, setParentEmail] = useState('');
  const [parentalConsentGiven, setParentalConsentGiven] = useState(false);
  const [countryCode, setCountryCode] = useState('GB'); // Default to UK
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Calculate age for conditional parental consent display
  const calculateAge = (dob: string) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
      age--;
    }
    return age;
  };
  const age = calculateAge(dateOfBirth);
  const requiresParentalConsent = age !== null && age >= 13 && age < 18;
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (session) {
        if (session.user.email?.toLowerCase() === 'support@therappi.app') {
          navigate('/whoop-dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    };
    checkAuth();
  }, []);
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Age verification
    if (!dateOfBirth) {
      setError('Please enter your date of birth.');
      return;
    }
    const userAge = calculateAge(dateOfBirth);
    if (userAge === null) {
      setError('Please enter a valid date of birth.');
      return;
    }
    if (userAge < 13) {
      setError('You must be at least 13 years old to use Therappi.');
      return;
    }

    // Check parental consent for minors
    if (userAge >= 13 && userAge < 18) {
      if (!parentEmail || !parentalConsentGiven) {
        setError('Parental consent is required for users under 18.');
        return;
      }
    }
    if (!privacyAccepted || !termsAccepted || !consentGiven) {
      setError('Please accept all required agreements to continue.');
      return;
    }
    setLoading(true);
    setError('');

    // Check for suspicious signup patterns via edge function
    try {
      const checkResponse = await supabase.functions.invoke('track-signup', {
        body: {
          action: 'check',
          email
        }
      });
      if (checkResponse.data?.blocked) {
        setError('We\'ve detected multiple signup attempts from your network. This is a security measure to prevent abuse. If you believe this is an error, please contact support@therappi.app for assistance.');
        setLoading(false);
        return;
      }
    } catch (checkError) {
      console.error('Failed to check signup eligibility:', checkError);
      // Continue with signup if check fails (don't block legitimate users due to technical issues)
    }
    try {
      const redirectUrl = getEmailRedirectUrl();
      const {
        data: signupData,
        error
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: displayName || email
          }
        }
      });

      // Track signup with IP address via edge function
      try {
        await supabase.functions.invoke('track-signup', {
          body: {
            userId: signupData?.user?.id,
            email,
            success: !error,
            error: error?.message || null
          }
        });
      } catch (trackError) {
        console.error('Failed to track signup:', trackError);
        // Don't fail the signup if tracking fails
      }
      if (error) {
        if (error.message.includes('already registered')) {
          setError('This email is already registered. Please use the login tab.');
        } else if (error.message.includes('Hook') || error.message.includes('hook') || error.message.includes('authorization token')) {
          setError('We\'re having trouble sending your confirmation email. Please try again in a moment.');
        } else if (error.message.includes('unexpected_failure')) {
          setError('Something went wrong. Please try again in a moment.');
        } else {
          setError(error.message);
        }
        return;
      }

      // Update user profile with consent and age verification information
      if (signupData?.user) {
        await supabase.from('profiles').update({
          consent_given: consentGiven,
          consent_date: new Date().toISOString(),
          privacy_policy_accepted: privacyAccepted,
          terms_accepted: termsAccepted,
          date_of_birth: dateOfBirth,
          country_code: countryCode,
          is_minor: userAge < 18,
          parental_consent_given: requiresParentalConsent ? parentalConsentGiven : null,
          parental_consent_date: requiresParentalConsent ? new Date().toISOString() : null,
          parent_guardian_email: requiresParentalConsent ? parentEmail : null,
          age_verified_at: new Date().toISOString()
        }).eq('user_id', signupData.user.id);
      }
      
      // Show prominent email confirmation dialog
      setSignupEmail(email);
      setShowEmailConfirmation(true);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials.');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please check your inbox and confirm your email before signing in.');
        } else if (error.message.includes('Hook') || error.message.includes('hook')) {
          setError('We\'re experiencing a temporary issue. Please try again in a moment.');
        } else if (error.message === 'Load failed' || error.message.includes('network') || error.message.includes('fetch')) {
          setError('Network connection issue. Please check your internet and try again.');
        } else {
          setError(error.message);
        }
        return;
      }

      // Use React Router navigation to preserve state machine flow
      if (data?.user?.email?.toLowerCase() === 'support@therappi.app') {
        // Give session time to sync then navigate
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/whoop-dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      // Handle iOS Safari "Load failed" network errors more gracefully
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage === 'Load failed' || errorMessage.includes('network')) {
        setError('Network connection issue. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) {
        setError(error.message);
        return;
      }
      setResetSuccess(true);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-muted/50 to-background">
      <SEO
        title="Sign in to Therappi® - Whole-Self Mental Wellness"
        description="Sign in or create your Therappi® account to begin AI-guided therapy across the five levels of the self."
        path="/auth"
        noindex
      />
      <h1 className="sr-only">Sign in to Therappi</h1>
      {/* Email Confirmation Dialog */}
      <Dialog open={showEmailConfirmation} onOpenChange={setShowEmailConfirmation}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center space-y-6 py-4">
            {/* Animated icon */}
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
              <div className="relative p-4 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary">
                <Mail className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            {/* Header */}
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-primary">Check Your Email</h2>
              <p className="text-lg text-muted-foreground">
                We've sent a confirmation link to:
              </p>
              <p className="text-lg font-medium text-foreground bg-muted/50 px-4 py-2 rounded-lg">
                {signupEmail}
              </p>
            </div>
            
            {/* Instructions */}
            <div className="space-y-3 w-full">
              <div className="flex items-start gap-3 text-left p-3 bg-muted/30 rounded-lg">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Click the confirmation link in your email to activate your Therappi® account
                </p>
              </div>
              <div className="flex items-start gap-3 text-left p-3 bg-muted/30 rounded-lg">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Check your spam folder if you don't see the email within a few minutes
                </p>
              </div>
            </div>
            
            {/* Action button */}
            <Button 
              onClick={() => setShowEmailConfirmation(false)}
              className="w-full"
              size="lg"
            >
              Got It - I'll Check My Email
            </Button>
            
            {/* Footer note */}
            <p className="text-xs text-muted-foreground">
              After confirming, return here to sign in
            </p>
          </div>
        </DialogContent>
      </Dialog>
      {/* Mobile Logo - edge-to-edge at top - ONLY shows on mobile */}
      <div className="lg:hidden w-full px-6 py-4">
        <img
          src={therappiTextLogo}
          alt="Therappi®"
          className="w-full max-w-xs mx-auto h-auto"
        />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl min-h-[700px] lg:min-h-[800px]" style={{
      paddingTop: '10px',
      paddingBottom: '60px'
    }}>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
          {/* Left side - Welcome and Features (Desktop only) */}
          <div className="hidden lg:block space-y-6 p-8">
          <div className="space-y-4">
            <p className="text-xl font-medium" style={{
              color: '#d5c5a1'
            }}>Where Clinical Psychology Meets Biometric Intelligence</p>
            
            <div className="space-y-4 pt-6">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Your AI Therapist That Actually Remembers</h3>
                  <p className="text-sm text-muted-foreground">Knows your story - your timeline, emotional patterns, health data, and healing goals. Choose your pathway: Person-Centred Support for reflection, CBT for rewiring patterns, Psychotherapy for unconscious roots, or QuantumSelf Psychology™ - where science, soul, and quantum principles converge to release the past and re-programme your future.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">The Sanctuary: Evidence-Based Crisis Tools</h3>
                  <p className="text-sm text-muted-foreground">
                    Developed by a Chartered Psychologist with over 25 years of clinical experience. Instant access to tools for anxiety, depression, trauma, grief, and stress - including grounding, panic protocols, behavioural activation, and trauma-processing exercises. Clinically grounded, spiritually attuned, and available whenever you need it most.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">WHOOP® × Biometric Intelligence</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect your wearable to reveal how mind and body move as one. See how meditation strengthens HRV, therapy improves recovery, and emotional shifts shape physical health - all through real-time biometric feedback.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Heart className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Predictive Wellness Intelligence</h3>
                  <p className="text-sm text-muted-foreground">
                    Eight-dimensional daily tracking that identifies imbalance before it escalates - detecting early signs of fatigue, stress, and burnout through integrated data and emotional trends. Because prevention is the new paradigm of healing.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Moon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Women's Health Ecosystem</h3>
                  <p className="text-sm text-muted-foreground">
                    Cycle, lunar, pregnancy, perimenopause, menopause - understand how hormonal rhythms influence your mood, energy, and emotional landscape. Align your biology with balance through personalised insights and practices.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Telempathy Lab: Where Science Meets Soul</h3>
                  <p className="text-sm text-muted-foreground">
                    Bridge intuition and intellect through structured practices that cultivate inner perception and energetic awareness. Explore the psychology of symbolism, track energetic frequency, and learn to interpret intuitive data as a deeper form of emotional intelligence.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="pt-6 border-t border-border/50 mt-6">
              <p className="text-base text-cyan-400 italic text-center">Because Healing The Mind Alone Was Never Enough.</p>
            </div>
          </div>
        </div>

          {/* Right side - Auth Card and Privacy */}
          <div className="flex flex-col items-center">
            {/* Logo above card - Desktop ONLY (hidden on mobile) */}
            <div
              className="hidden lg:flex items-center justify-center mb-8"
              style={{
                zIndex: 2,
                position: 'relative',
              }}
            >
              <img
                src={therappiTextLogo}
                alt="Therappi®"
                className="w-full max-w-md h-auto object-contain"
              />
            </div>

          <Card className="w-full max-w-md mx-auto shadow-2xl lg:translate-y-[0px] translate-y-[-30px]" style={{
            zIndex: 1,
            position: 'relative'
          }}>
          <CardHeader className="text-center space-y-2 sm:space-y-3">
            <div className="flex items-center justify-center mb-2">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-2" />
              <CardTitle className="text-xl sm:text-2xl">Secure Access</CardTitle>
            </div>
            <CardDescription className="text-sm sm:text-base">
              Begin your transformational journey in a safe, private space
            </CardDescription>
          </CardHeader>
        
        <CardContent className="px-5 sm:px-6">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-lg h-16 p-2">
              <TabsTrigger value="login" className="h-full text-sm font-medium rounded-md">Sign In</TabsTrigger>
              <TabsTrigger value="signup" className="h-full text-sm font-medium rounded-md">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              {showForgotPassword ? <div className="space-y-4">
                  {resetSuccess ? <div className="text-center py-6 space-y-4">
                      <div className="p-3 rounded-full bg-primary/10 w-16 h-16 mx-auto flex items-center justify-center">
                        <Heart className="h-8 w-8 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">Check Your Email</h3>
                        <p className="text-sm text-muted-foreground">
                          We've sent a password reset link to <strong>{resetEmail}</strong>
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Click the link in the email to reset your password.
                        </p>
                      </div>
                      <Button onClick={() => {
                        setShowForgotPassword(false);
                        setResetSuccess(false);
                        setResetEmail('');
                      }} variant="outline" className="mt-4">
                        Back to Login
                      </Button>
                    </div> : <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="reset-email">Email Address</Label>
                        <Input id="reset-email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required placeholder="your@email.com" />
                        <p className="text-xs text-muted-foreground">
                          Enter your email address and we'll send you a link to reset your password.
                        </p>
                      </div>

                      {error && <Alert variant="destructive">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>}

                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? 'Sending...' : 'Send Reset Link'}
                      </Button>

                      <Button type="button" onClick={() => {
                        setShowForgotPassword(false);
                        setError('');
                      }} variant="ghost" className="w-full">
                        Back to Login
                      </Button>
                    </form>}
                </div> : <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-primary hover:underline">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" className="pr-10" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {error && <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>}
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input id="displayName" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="How should we address you?" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input id="signup-password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" minLength={6} className="pr-10" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Age Verification */}
                <div className="pt-4 border-t">
                  <AgeVerification dateOfBirth={dateOfBirth} setDateOfBirth={setDateOfBirth} parentEmail={parentEmail} setParentEmail={setParentEmail} parentalConsentGiven={parentalConsentGiven} setParentalConsentGiven={setParentalConsentGiven} showParentalConsent={requiresParentalConsent} countryCode={countryCode} setCountryCode={setCountryCode} />
                </div>

                {/* Compliance Agreements */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Required Agreements</Label>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Checkbox id="privacy" checked={privacyAccepted} onCheckedChange={checked => setPrivacyAccepted(checked === true)} />
                      <Label htmlFor="privacy" className="text-sm leading-5">
                        I accept the <button type="button" onClick={() => navigate('/privacy')} className="text-primary hover:underline">Privacy Policy</button> and understand how my data will be protected
                      </Label>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Checkbox id="terms" checked={termsAccepted} onCheckedChange={checked => setTermsAccepted(checked === true)} />
                      <Label htmlFor="terms" className="text-sm leading-5">
                        I agree to the <button type="button" onClick={() => navigate('/terms')} className="text-primary hover:underline">Terms of Service</button>
                      </Label>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Checkbox id="consent" checked={consentGiven} onCheckedChange={checked => setConsentGiven(checked === true)} />
                      <Label htmlFor="consent" className="text-sm leading-5">
                        I consent to the processing of my health-related information for therapeutic purposes, as described in our privacy policy
                      </Label>
                    </div>
                  </div>
                </div>

                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>Your Privacy Matters:</strong> All conversations are encrypted and stored securely. You can request data deletion at any time.
                  </AlertDescription>
                </Alert>

                {error && <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>}

                <Button type="submit" className="w-full" disabled={loading || !privacyAccepted || !termsAccepted || !consentGiven || !dateOfBirth || age !== null && age < 13 || requiresParentalConsent && (!parentEmail || !parentalConsentGiven)}>
                  {loading ? 'Creating Account...' : 'Create Secure Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          {/* Trust Indicators */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                <span>Encrypted</span>
              </div>
              <div className="flex items-center gap-1">
                <Lock className="h-3 w-3" />
                <span>HIPAA Compliant</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                <span>Trusted by 10,000+</span>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>

            {/* Privacy section below card - Desktop only */}
            <div className="hidden lg:block max-w-md mx-auto text-center" style={{
            marginTop: '33px'
          }}>
              <h3 className="font-semibold mb-1 text-sm">Clinical-Grade Privacy & Security</h3>
              <p className="text-xs text-muted-foreground">
                End-to-end encrypted. GDPR-compliant.<br />
                Your data, your healing - held in total confidentiality.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
      
      {/* Logo Editor Controls - Only show when authenticated */}
      {isAuthenticated && <LogoEditorControls isEditing={isEditing} onToggle={() => setIsEditing(!isEditing)} />}
    </div>;
}